// Every brand tonality derives from the one configurable accentColor; app.css's teal defaults are the pre-hydration fallback.

const DEFAULT_ACCENT = '#33ccaa' // brand teal — equals the '#3ca' store/SDK default

/** Expand `#rgb` → `#rrggbb`, l-case, prefix `#`. Returns null for anything unparseable. */
export function normalizeHex(input: string | null | undefined): string | null {
  if (!input) return null
  let hex = input.trim().toLowerCase()
  if (!hex.startsWith('#')) hex = `#${hex}`
  if (/^#[0-9a-f]{3}$/.test(hex)) hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : null
}

type Rgb = readonly [number, number, number]

function hexToRgb(hex: string): Rgb {
  const v = Number.parseInt(hex.slice(1), 16)
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255]
}

/** Multiply toward black: `t=0.12` → 88% brightness. */
const shade = (rgb: Rgb, t: number): Rgb => rgb.map((c) => c * (1 - t)) as unknown as Rgb

/** Mix toward white: `t=0.85` → 85% of the way to #fff. */
const tint = (rgb: Rgb, t: number): Rgb => rgb.map((c) => c + (255 - c) * t) as unknown as Rgb

/** Trim float noise so `99.96000000000001` serializes as `99.96`. */
const fmt = (n: number) => Number(n.toFixed(2)).toString()

const triplet = (rgb: Rgb) => rgb.map(fmt).join(' ')
const rgbFn = (rgb: Rgb) => `rgb(${triplet(rgb)})`

/** Linearize one 0-255 sRGB channel (WCAG). Shared with color-field's contrast math. */
export function srgbChannelToLinear(c: number): number {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance, 0 (black) … 1 (white). */
function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex)
  return 0.2126 * srgbChannelToLinear(r) + 0.7152 * srgbChannelToLinear(g) + 0.0722 * srgbChannelToLinear(b)
}

/** Does the accent need dark text on top? Threshold L>0.179 is the black-vs-white WCAG cross-over. */
export function prefersDarkText(hex: string): boolean {
  return luminance(hex) > 0.179
}

/**
 * Upper bound on memoized palettes. Accents are few — one per app config plus whatever the SDK's
 * `customize` postMessage pushes — so this is only a guard against an unbounded stream of distinct
 * inputs, not a working set to tune. When exceeded the whole map is dropped rather than evicted
 * one-by-one: recomputing 64 palettes is cheaper than tracking recency.
 */
export const ACCENT_THEME_CACHE_LIMIT = 64

const accentThemeCache = new Map<string, Readonly<Record<string, string>>>()

/**
 * The CSS custom properties an accent color resolves to. Set on `<html>` so both light and dark mode
 * inherit them; invalid or empty input falls back to the brand teal, which is exactly the app.css
 * defaults. Memoized on the NORMALIZED hex, since `__root` calls this on every SSR request and every
 * client re-render — the same accent returns the same frozen object.
 */
export function accentThemeVars(input: string | null | undefined): Readonly<Record<string, string>> {
  const hex = normalizeHex(input) ?? DEFAULT_ACCENT
  const cached = accentThemeCache.get(hex)
  if (cached) return cached
  if (accentThemeCache.size >= ACCENT_THEME_CACHE_LIMIT) accentThemeCache.clear()
  const vars = Object.freeze(computeAccentThemeVars(hex))
  accentThemeCache.set(hex, vars)
  return vars
}

function computeAccentThemeVars(hex: string): Record<string, string> {
  const base = hexToRgb(hex)
  const vars: Record<string, string> = {
    '--primary': hex,
    // SVG logos/icons + selectors read these as `rgb(var(--…))` triplets.
    '--color-primary': triplet(base),
    '--color-primary-rgb': triplet(base),
    '--color-accent-rgb': triplet(base),
    '--color-primary-200': triplet(shade(base, 0.12)),
    '--color-primary-less-darker': rgbFn(shade(base, 0.06)),
    '--color-primary-lighter': rgbFn(tint(base, 0.12)),
    '--color-primary-more-lighter': rgbFn(tint(base, 0.24)),
    '--color-primary-lightest': rgbFn(tint(base, 0.85)),
  }
  // Only override on-accent text for a custom accent; the default defers to app.css (see header).
  if (hex !== DEFAULT_ACCENT) {
    const dark = prefersDarkText(hex)
    vars['--primary-foreground'] = dark ? 'oklch(0.205 0 0)' : 'oklch(0.985 0 0)'
    vars['--accent-bg-text-color'] = dark ? '#000' : '#fff'
  }
  return vars
}
